import { formatInTimeZone } from 'date-fns-tz';
import { roundToNearestMinutes, differenceInYears, isBefore, isAfter, parse, isValid, isWithinInterval, startOfDay, addDays, subYears, fromUnixTime } from 'date-fns';
import { fr as frLocale } from 'date-fns/locale/fr';
import router from '@/router';
import store from '@/store';
import countriesJson from './countries.json';

const { countries } = countriesJson;

const mainUrl = 'https://media.ucpa.com';

const optionTypes = { MANDATORY: 'MANDATORY', OPTIONAL: 'OPTIONAL' };

const moveTo404 = () => {
  router.replace({ name: 'Catcher' });
};
const generateBaseUrl = path => encodeURI(mainUrl + path);
const generateImageUrl = (path, transformation = null) => generateBaseUrl((transformation ? `/${transformation}` : '') + path);
const frenchTime = (timestamp, formatTime, timezone = 'Europe/Paris') => formatInTimeZone(roundToNearestMinutes(new Date(timestamp)), timezone, formatTime, { locale: frLocale });

const backToAlpha = () => {
  window.location.href = store.getters.alphaURL;
};

const lengthBetween = (str, min, max) => (str ? str.length >= min && str.length <= max : false);

const computeSalesStartDate = period => {
  const startDate = new Date(period.salesStartDate);

  if (period.salesStartTime) {
    const [hours, minutes] = period.salesStartTime.split(':');
    startDate.setHours(hours);
    startDate.setMinutes(minutes);
  }
  return startDate;
};

const computedOfferPrices = (currentParticipant, offer = store.state.offerInfo) => {
  const { impersonateData } = store.state;

  const prices = offer && offer.detail ? offer.detail.prices : [];

  const date = currentParticipant.birth_date.split('/').reverse().join('/');

  let filteredPrices = prices.filter(p => {
    if (!p.priceCategory) {
      return p;
    }

    if (!p.period) {
      return p;
    }

    if (store.state.cartItem.participants.length && !(store.state?.cartItem?.participants || []).some(participant => participant.tariff.period.uuid === p.period.uuid)) {
      return null;
    }

    if (isBefore(new Date(p.period.validityEndDate), new Date())) {
      return null;
    }

    if (isAfter(computeSalesStartDate(p.period), new Date())) {
      return null;
    }

    const participantYear = differenceInYears(new Date(), new Date(date));
    if (impersonateData) {
      return p;
    }

    if (p.priceCategory.ageMin && p.priceCategory.ageMax) {
      if (participantYear <= p.priceCategory.ageMax && participantYear >= p.priceCategory.ageMin) {
        return p;
      }
      return null;
    }

    if (p.priceCategory.ageMin) {
      return participantYear >= p.priceCategory.ageMin;
    }
    if (p.priceCategory.ageMax) {
      return participantYear <= p.priceCategory.ageMax;
    }

    return null;
  });

  filteredPrices = filteredPrices.map((p, index) => {
    const selectedTariff = (offer?.selectedHoranetTariffs || []).find(tariff => tariff.codeTariff === p.tariffCode);
    if (selectedTariff) {
      return { ...p, index, ...selectedTariff };
    }
    return { ...p, index };
  });

  return filteredPrices.sort((a, b) => b.price - a.price);
};

const capitalizeFirstLetter = str => str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();

const formFields = {
  createCustomer: [
    [
      { label: 'Civilité', key: 'gender', type: 'radio', value: null, required: true, options: [{ value: 'mr', text: 'M' }, { value: 'mme', text: 'Mme', style: 'ml3' }] }
    ],
    [
      { label: 'Nom', key: 'lastname', type: 'text', value: null, required: true },
      { label: 'Prénom', key: 'firstname', type: 'text', value: null, required: true }
    ],
    [
      { label: 'Date de naissance', key: 'birth_date', type: 'text', value: null, required: true, placeholder: 'JJ/MM/AAAA' }
    ],
    [
      { label: 'Téléphone portable', key: 'mobilephone', type: 'text', value: null, required: true }
    ],
    [
      { label: 'Numéro', key: 'street_number', type: 'text', value: null, required: true },
      { label: 'Voie', key: 'street_name', type: 'text', value: null, required: true }
    ],
    [
      { label: 'Complément d\'adresse', key: 'address2', type: 'text', value: null, required: false }
    ],
    [
      { label: 'Code Postal', key: 'zip_code', type: 'text', value: null, required: true },
      { label: 'Ville', key: 'town', type: 'text', value: null, required: true }
    ],
    [
      { label: 'Pays', key: 'country', type: 'select', value: null, required: true, options: countries }
    ]
  ],
  editCustomer: [
    [
      { label: 'Civilité', key: 'gender', type: 'radio', value: null, required: true, options: [{ value: 'mr', text: 'M' }, { value: 'mme', text: 'Mme', style: 'ml3' }] }
    ],
    [
      { label: 'Nom', key: 'lastname', type: 'text', value: null, required: true },
      { label: 'Prénom', key: 'firstname', type: 'text', value: null, required: true }
    ],
    [
      { label: 'Téléphone portable', key: 'mobilephone', type: 'text', value: null, required: true }
    ],
    [
      { label: 'Numéro', key: 'street_number', type: 'text', value: null, required: true },
      { label: 'Voie', key: 'street_name', type: 'text', value: null, required: true }
    ],
    [
      { label: 'Complément d\'adresse', key: 'address2', type: 'text', value: null, required: false }
    ],
    [
      { label: 'Code Postal', key: 'zip_code', type: 'text', value: null, required: true },
      { label: 'Ville', key: 'town', type: 'text', value: null, required: true }
    ],
    [
      { label: 'Pays', key: 'country', type: 'select', value: null, required: true, options: countries }
    ]
  ],
  emergencyContact: [
    [
      { label: 'Nom', key: 'lastname', type: 'text', value: null, required: true, placeholder: 'Compléter le nom du contact d\'urgence' },
      { label: 'Prénom', key: 'firstname', type: 'text', value: null, required: true, placeholder: 'Compléter le prénom du contact d\'urgence' }
    ],
    [
      { label: 'Email', key: 'email', type: 'text', value: null, required: true, placeholder: 'Compléter l\'adresse email' },
      { label: 'Confirmation de l\'email', key: 'email_confirmation', type: 'text', value: null, required: true, placeholder: 'Compléter l\'adresse email', ignoreValidation: true }
    ],
    [
      { label: 'Téléphone portable', key: 'telephone', type: 'text', value: null, required: true }
    ]
  ]
};

const checkError = (value, type, mobileValidation = false, required = false) => {
  let birth_date;
  const dateInstance = new Date();

  // Not required and doesn't have value
  if (!required && !value) {
    return null;
  }

  switch (type) {
    case 'firstname':
      if (required && value === '') {
        return 'Veuillez renseigner votre prénom';
      }

      if (value && value.length < 2) {
        return 'Le prénom doit contenir au moins 2 caractères';
      }
      return !(/^(["A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/).test(value) ? 'Un prénom ne peut être composé que de lettres et des caractères " - \'' : false;

    case 'lastname':
      if (required && value === '') {
        return 'Veuillez renseigner votre nom';
      }

      if (value && value.length < 2) {
        return 'Le nom doit contenir au moins 2 caractères';
      }
      return !(/^(["A-Za-z-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/).test(value) ? 'Un nom ne peut être composé que de lettres et des caractères " - \'' : false;

    case 'birth_date':
      if (required && value === '') {
        return 'Veuillez renseigner une date de naissance';
      }

      birth_date = parse(value, 'dd/MM/yyyy', startOfDay(dateInstance));

      if (!isValid(birth_date)) {
        return 'Merci de respecter le format jj/mm/aaaa';
      }

      if (!isWithinInterval(birth_date, { start: addDays(subYears(startOfDay(dateInstance), 100), 1), end: startOfDay(dateInstance) })) {
        return 'La date de naissance est incorrecte';
      }

      return false;

    case 'email':
      if (required && value === '') {
        return "L'email doit être renseigné";
      }
      return !(/^(([^<>()[\].,;:\s@"]+(\.[^<>()[\].,;:\s@"]+)*)|(".+"))@(([^<>()[\].,;:\s@"]+\.)+[^<>()[\].,;:\s@"]{2,})$/).test(value) ? 'L\'email doit comprendre une @, ainsi qu\'un nom de domaine (.com, .fr, ...)' : false;

    case 'telephone':
    case 'mobilephone':
      if (required && value === '') {
        return 'Veuillez renseigner un numéro';
      }

      if (!(/^([0-9+\s]{1,})$/).test(value)) {
        return 'Le numéro ne doit être composé que de chiffres.';
      }
      return !mobileValidation ? 'Veuillez renseigner un numéro valide' : false;

    case 'town':
      if (required && value === '') {
        return 'Veuillez renseigner la ville';
      }

      if (!(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/).test(value)) {
        return 'Une ville ne peut être composé que de lettres et des caractères " - \'';
      }

      return !lengthBetween(value, 1, 30) ? 'La ville doit avoir entre 1 et 30 caractères' : null;

    case 'street_name':
      if (required && value === '') {
        return 'Veuillez renseigner un nom de rue';
      }

      if (!(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/).test(value)) {
        return 'Une ville ne peut être composé que de lettres et des caractères " - \'';
      }

      return !lengthBetween(value, 1, 30) ? 'La ville doit avoir entre 1 et 30 caractères' : null;

    case 'street_number':
      if (required && value === '') {
        return 'Veuillez renseigner un numéro de rue';
      }

      if (!(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s.,]*){1,}$/).test(value)) {
        return 'Le nº(rue, voie) ne peut être composé que de lettres et de caractères " - \' , .';
      }

      return !lengthBetween(value, 1, 12) ? 'Le numéro de rue doit avoir entre 1 et 12 caractères' : null;
    case 'address1':
      if (required && value === '') {
        return "Veuillez renseigner l'adresse";
      }

      if (!(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/).test(value)) {
        return 'Une adresse ne peut être composé que de lettres et des caractères " - \'';
      }

      return !lengthBetween(value, 1, 30) ? "L'adresse postale doit avoir entre 1 et 30 caractères" : null;

    case 'address2':
      if (required && value === '') {
        return "Veuillez renseigner un complément d'adresse";
      }

      if (!(/^(["A-Za-z0-9-'\u00C0-\u00D6\u00D8-\u00f6\u00f8-\u00ff\s]*){2,}$/).test(value)) {
        return 'Une adresse ne peut être composé que de lettres et des caractères " - \'';
      }

      return !lengthBetween(value, 0, 30) ? "L'adresse postale doit avoir entre 0 et 30 caractères" : null;

    case 'zip_code':
      if (required && value === '') {
        return 'Le code postal doit être renseigné';
      }

      return !(/^([A-Z a-z0-9]){3,10}$/).test(value) ? 'Le code postal doit être composé uniquement de chiffres' : null;

    case 'country':
      return !value ? 'Veuillez compléter votre pays' : null;

    default:
  }

  return null;
};

/* Form validation before the first input is made in each field */
const getRequiredFields = (type, extraFields = []) => {
  const requiredFields = [];

  (formFields[type] || []).forEach(line => {
    line.forEach(field => {
      if (field.required && !field.ignoreValidation) {
        requiredFields.push(field.key);
      }
    });
  });
  return [...requiredFields, ...extraFields];
};

const computeFunnelType = () => {
  const { newTDA, subscriptionTDA } = store.state;

  if (subscriptionTDA) {
    return 'tunnel abonnement';
  } if (newTDA) {
    return 'tunnel entrées unitaires';
  }
  return 'tunnel classique';
};

const horseOptions = [
  { label: 'Cheval UCPA', value: 'UCPA', info: '(cheval mis à disposition par l’UCPA)' },
  { label: 'Cheval Propriétaire', value: 'OWNER' }
];

const computePriceLabel = price => parseFloat(price / 100).toFixed(2);

const computeCourtBookingDetails = () => {
  const { siteInfo, user, offerInfo, product, courtSettings } = store.state;
  let maxPrice = Number.MIN_VALUE;

  let maxPriceObject = {};

  let productImage = null;
  if (product.header_images && product.header_images.length) {
    productImage = generateImageUrl(product.header_images[0], 't_UCPA_Vertical');
  } else if (product.headerImagesSEO && product.headerImagesSEO.length && product.headerImagesSEO[0].url) {
    productImage = generateImageUrl(product.headerImagesSEO[0].url, 't_UCPA_Vertical');
  }
  offerInfo.detail.prices.forEach(p => {
    if (p.price > maxPrice && !p.isGymlibTariff) {
      maxPrice = p.price;
      maxPriceObject = p;
    }
  });

  let { negotiatedPrice } = courtSettings;
  if (!negotiatedPrice) {
    const gymLibNegotiated = courtSettings.gymLibTariff?.negotiatedPrice;
    negotiatedPrice = courtSettings.haveGymLibCode && courtSettings.isPromoCodeValid && gymLibNegotiated != null ? parseInt(gymLibNegotiated * 100) : null;
  }

  return {
    isInternalSession: siteInfo.is_internal_session,
    sessionId: courtSettings.session?.code || courtSettings.session?.id,
    haveGymLibCode: courtSettings.haveGymLibCode,
    promoCode: courtSettings.promoCode,
    workspace: siteInfo.workspace,
    haveSubscription: courtSettings.haveSubscription,
    cardContract: courtSettings.selectedCardContract ? parseInt(courtSettings.selectedCardContract) : null,
    cardParticipationUnits: courtSettings.cardParticipationUnits || 0,
    site: {
      sitePreRoute: siteInfo.site_preroute,
      siteUrl: siteInfo.site_url
    },
    reservationInfo: {
      firstname: user.firstname,
      lastname: user.lastname,
      birthDate: user.birth_date,
      isPlaying: !courtSettings.isInvite ? courtSettings.isCaptainPlaying : true,
      uuid: siteInfo.is_internal_session ? user.uuid : user.horanet_id,
      horanetId: user.horanet_id,
      customerUuid: user.uuid
    },
    details: {
      productUuid: product.uuid,
      productName: product.name,
      productUrl: product.url,
      productImage: productImage || null,
      offerFilliere: offerInfo.filliere,
      contremarque: offerInfo.contremarque,
      sansSupport: offerInfo.sans_support,
      cancelation: offerInfo.cancelation,
      horanetProductCode: offerInfo.horanet_product.horanet_product_code,
      horanetProductReference: offerInfo.horanet_product.horanet_product_reference,
      horanetProductName: offerInfo.horanet_product.name,
      priceCategory: courtSettings.tariff.category || courtSettings.tariff.priceCategory,
      tariffCode: courtSettings.tariff.tariffCode,
      tariffCategoryCode: courtSettings.tariff.tariffCategoryCode,
      price: courtSettings.tariff.price,
      negotiatedPrice,
      maxPrice: maxPrice,
      // maxPriceTarrifCode: maxPriceObject.tariffCode,
      tva: courtSettings.tariff.tva,
      quantity: courtSettings.partsQuantity || 1,
      unitSales: offerInfo.detail.unit_sales,
      pass: offerInfo.detail.pass,
      firstPurchase: offerInfo.detail.first_purchase,
      reloadable: offerInfo.detail.reloadable,
      inscription: offerInfo.detail.inscription,
      reservation: offerInfo.detail.reservation,
      recurring: offerInfo.detail.recurring
    },
    options: (courtSettings.selectedOptions || []).map(option => ({
      cancelation: option.cancelation,
      name: option.name,
      horanetProductCode: option.horanet_product.horanet_product_code,
      horanetProductReference: option.horanet_product.horanet_product_reference,
      horanetProductName: option.horanet_product.name,
      priceCategory: option.price.category || option.price.priceCategory,
      tariffCode: option.price.tariffCode,
      tariffCategoryCode: option.price.tariffCategoryCode,
      price: option.price.price,
      tva: option.price.tva,
      quantity: option.quantity,
      unitSales: option.detail.unit_sales,
      pass: option.detail.pass,
      firstPurchase: option.detail.first_purchase,
      reloadable: option.detail.reloadable,
      inscription: option.detail.inscription,
      reservation: option.detail.reservation,
      recurring: option.detail.recurring
    }))
  };
};

const isSessionValid = (session, isInvite) => {
  const { user } = store.state;
  const { is_internal_session } = store.state.siteInfo;
  const uuid = is_internal_session ? user.uuid : user.horanet_id;
  const doesSessionHaveStock = session.placesDisponibles != null
    ? session.placesDisponibles > 0
    : session.max_participant > (session.participants || []).length;

  if (isInvite === 'false' && session?.captain?.uuid) {
    return false;
  }

  if (session?.captain?.uuid && session?.captain?.uuid === uuid) {
    return false;
  }

  const isParticipantOnSession = session.allParticipants ? (session.allParticipants || []).some(p => p === uuid) : (session.participants || []).some(p => p.uuid === uuid);

  if (isParticipantOnSession) {
    return false;
  }

  if (!doesSessionHaveStock) {
    return false;
  }

  if (isBefore(fromUnixTime(session.start_time), new Date())) {
    return false;
  }

  if (session.cancelled) {
    return false;
  }

  return true;
};

const assetsPath = () => '/loisirs-reservation/';

export {
  isSessionValid,
  computeCourtBookingDetails,
  computePriceLabel,
  computedOfferPrices,
  optionTypes,
  moveTo404,
  generateImageUrl,
  frenchTime,
  backToAlpha,
  capitalizeFirstLetter,
  formFields,
  lengthBetween,
  checkError,
  getRequiredFields,
  computeFunnelType,
  horseOptions,
  computeSalesStartDate,
  assetsPath
};
